<script>
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import { formatDistanceToNow } from "date-fns";

  import RecentActivityModal from "./RecentActivityModal.svelte";

  let { socialProfileId, visibleCount = 5 } = $props();

  let activities = $state([]);
  let loading = $state(true);
  let error = $state(null);
  let modalOpen = $state(false);
  let currentPage = $state(0);

  let visibleActivities = $derived(
    getGroupedActivities().slice(0, visibleCount)
  );

  let refreshInterval;

  $effect(() => {
    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  });

  onMount(async () => {
    await fetchActivities();
    refreshInterval = setInterval(() => fetchActivities(0, false), 1000 * 60);
  });

  async function fetchActivities(page = 0, showLoading = true) {
    try {
      if (showLoading) loading = true;

      currentPage = page;

      let url = `/api/recent_activities?page=${page}`;

      if (socialProfileId) {
        url += `&social_profile_id=${socialProfileId}`;
      }

      const response = await fetch(url);

      if (!response.ok) throw new Error("Failed to fetch activities");
      const newActivities = await response.json();

      if (page === 0) {
        activities = newActivities;
      } else {
        activities = [...activities, ...newActivities];
      }

      error = null;
    } catch (err) {
      error = err.message;
      return [];
    } finally {
      loading = false;
    }
  }

  function getGroupedActivities() {
    const groupedMap = new Map();

    activities.forEach((activity) => {
      const timestamp = new Date(activity.created_at);
      const timeKey = Math.floor(timestamp.getTime() / (1000 * 60 * 60));

      let entityId = null;
      let entityType = null;

      if (activity.instagram_profile_parsed_id) {
        entityId = activity.instagram_profile_parsed_id;
        entityType = "profile";
      } else if (activity.instagram_post_parsed_id) {
        entityId = activity.instagram_post_parsed_id;
        entityType = "post";
      } else if (activity.instagram_reel_parsed_id) {
        entityId = activity.instagram_reel_parsed_id;
        entityType = "reel";
      }

      if (!entityId) {
        const uniqueKey = `individual_${activity.id}`;
        groupedMap.set(uniqueKey, {
          id: uniqueKey,
          activities: [activity],
          created_at: activity.created_at,
          username: activity.username,
          image_url: activity.image_url,
          entityType,
        });
        return;
      }

      const key = `${entityType}_${entityId}_${timeKey}`;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          id: key,
          activities: [activity],
          created_at: activity.created_at,
          username: activity.username,
          image_url: activity.image_url,
          entityType,
        });
      } else {
        groupedMap.get(key).activities.push(activity);
      }
    });

    return Array.from(groupedMap.values()).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }

  function openActivityModal() {
    modalOpen = true;
  }

  function closeActivityModal() {
    modalOpen = false;
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num;
  };

  const getTimeAgo = (dateString) => {
    try {
      const formattedTime = formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
      });
      return formattedTime !== "less than a minute ago"
        ? formattedTime
        : "just now";
    } catch (e) {
      return "just now";
    }
  };

  const activityDescriptions = {
    new_profile: "New profile",
    new_post: "New post",
    new_reel: "New reel",
    followers_changed: "Followers updated",
    followings_changed: "Following updated",
    posts_count_changed: "Posts updated",
    engagement_changed: "Engagement updated",
    post_likes_count_changed: "Post likes updated",
    post_comments_count_changed: "Post comments updated",
    reel_likes_count_changed: "Reel likes updated",
    reel_comments_count_changed: "Reel comments updated",
    reel_plays_count_changed: "Reel plays updated",
  };

  function getActivityDescription(activity) {
    return activityDescriptions[activity.action] || activity.action;
  }

  function getActivityBadges(activity) {
    const badges = [];

    switch (activity.action) {
      case "new_profile":
        badges.push({
          text: `${formatNumber(activity.data.followers_count)} followers`,
          color: "bg-gray-100 text-gray-800",
        });
        badges.push({
          text: `${formatNumber(activity.data.following_count)} following`,
          color: "bg-gray-100 text-gray-800",
        });
        badges.push({
          text: `${formatNumber(activity.data.posts_count)} posts`,
          color: "bg-gray-100 text-gray-800",
        });
        break;
      case "new_post":
        badges.push({
          text: `${formatNumber(activity.data.likes_count)} likes`,
          color: "bg-gray-100 text-gray-800",
        });
        badges.push({
          text: `${formatNumber(activity.data.comments_count)} comments`,
          color: "bg-gray-100 text-gray-800",
        });
        break;
      case "new_reel":
        badges.push({
          text: `${formatNumber(activity.data.likes_count)} likes`,
          color: "bg-gray-100 text-gray-800",
        });
        badges.push({
          text: `${formatNumber(activity.data.comments_count)} comments`,
          color: "bg-gray-100 text-gray-800",
        });
        badges.push({
          text: `${formatNumber(activity.data.play_count)} plays`,
          color: "bg-gray-100 text-gray-800",
        });
        break;
      default:
        if (activity.action.includes("_changed")) {
          const diff = activity.data.current - activity.data.previous;
          const prefix = diff > 0 ? "+" : "";
          const color =
            diff > 0
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800";

          let text = "";
          if (activity.action === "followers_changed") {
            text = `${prefix}${diff} followers`;
          } else if (activity.action === "followings_changed") {
            text = `${prefix}${diff} following`;
          } else if (activity.action === "posts_count_changed") {
            text = `${prefix}${diff} posts`;
          } else if (activity.action === "engagement_changed") {
            text = `${prefix}${diff.toFixed(2)}% engagement`;
          } else if (activity.action.includes("likes_count_changed")) {
            text = `${prefix}${diff} likes`;
          } else if (activity.action.includes("comments_count_changed")) {
            text = `${prefix}${diff} comments`;
          } else if (activity.action === "reel_plays_count_changed") {
            text = `${prefix}${diff} plays`;
          }

          if (text) {
            badges.push({ text, color });
          }
        }
    }

    return badges;
  }

  function getGroupDescription(group) {
    if (group.activities.length === 1) {
      return getActivityDescription(group.activities[0]);
    }

    const entityTypeDescriptions = {
      post: "Post updated",
      reel: "Reel updated",
      profile: "Profile updated",
    };

    return entityTypeDescriptions[group.entityType] || "Updates";
  }

  function getGroupBadges(group) {
    if (group.activities.length === 1) {
      return getActivityBadges(group.activities[0]);
    }

    const badges = [];

    group.activities.forEach((activity) => {
      if (activity.action.includes("_changed")) {
        const diff = activity.data.current - activity.data.previous;
        if (diff === 0) return;

        const prefix = diff > 0 ? "+" : "-";
        const color =
          diff > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";

        let text = "";
        if (activity.action === "followers_changed") {
          text = `${prefix}${Math.abs(diff)} followers`;
        } else if (activity.action === "followings_changed") {
          text = `${prefix}${Math.abs(diff)} following`;
        } else if (activity.action === "posts_count_changed") {
          text = `${prefix}${Math.abs(diff)} posts`;
        } else if (activity.action === "engagement_changed") {
          text = `${prefix}${Math.abs(diff).toFixed(2)}% engagement`;
        } else if (activity.action.includes("likes_count_changed")) {
          text = `${prefix}${Math.abs(diff)} likes`;
        } else if (activity.action.includes("comments_count_changed")) {
          text = `${prefix}${Math.abs(diff)} comments`;
        } else if (activity.action === "reel_plays_count_changed") {
          text = `${prefix}${Math.abs(diff)} plays`;
        }

        if (text) {
          badges.push({ text, color });
        }
      }
    });

    return badges;
  }
</script>

<div class="recent-activity-container">
  <h2 class="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h2>

  {#if loading}
    <div class="flex justify-center items-center h-24">
      <div
        class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"
      ></div>
    </div>
  {:else if error}
    <div
      class="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 text-sm rounded shadow-sm"
      role="alert"
    >
      <p>{error}</p>
    </div>
  {:else if activities.length === 0}
    <div
      class="bg-gray-50 p-6 rounded-md text-center border border-gray-100 shadow-sm mt-6"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-12 w-12 mx-auto text-gray-400 mb-3"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p class="text-gray-600 font-medium mb-1">No recent activities yet</p>
      <p class="text-gray-500 text-sm">
        Check back later as new activities are tracked
      </p>
    </div>
  {:else}
    <div class="divide-y divide-gray-200">
      {#each visibleActivities as group (group.id)}
        {@const badges = getGroupBadges(group)}

        <div
          class="py-3 hover:bg-gray-50 transition-colors"
          transition:fade={{ duration: 200 }}
        >
          <div class="flex items-start gap-3">
            <div class="relative">
              <img
                src={group.image_url || "https://via.placeholder.com/40"}
                alt={group.username}
                class="w-17 h-17 min-w-17 min-h-17 rounded-lg object-cover flex-shrink-0"
              />
            </div>

            <div class="flex-grow min-w-0">
              <div class="flex justify-between items-center mb-0.5">
                <div class="flex items-center">
                  <span class="font-medium text-gray-900 text-sm"
                    >@{group.username}</span
                  >
                </div>
                <span class="text-xs text-gray-500 ml-1 flex-shrink-0"
                  >{getTimeAgo(group.created_at)}</span
                >
              </div>

              <p class="text-sm text-gray-700 mb-2">
                {getGroupDescription(group)}
              </p>

              <div class="mt-1 flex flex-wrap gap-1.5">
                {#each badges as badge}
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full {badge.color}"
                  >
                    {badge.text}
                  </span>
                {/each}
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>

    {#if activities.length > 5}
      <div class="mt-2 text-center">
        <button
          onclick={openActivityModal}
          class="text-indigo-600 hover:text-indigo-800 text-sm font-medium px-4 py-2 rounded-md hover:bg-indigo-50 transition-colors focus:outline-none cursor-pointer"
        >
          View All Activity
        </button>
      </div>
    {/if}
  {/if}

  <RecentActivityModal
    {socialProfileId}
    {activities}
    {fetchActivities}
    {getActivityBadges}
    {currentPage}
    isOpen={modalOpen}
    onClose={closeActivityModal}
  />
</div>

<style>
  .recent-activity-container {
    width: 100%;
  }
</style>
